/**
 * Gestionnaire d'abonnement V3 - Design identique aux screens
 * Adapté au nouveau système : Branding Pro (30€) + Seats (19€) + Crédits dégressifs
 */

import { useState, useEffect } from "react";
import { Search, Calendar, Download, Zap } from "lucide-react";
import { Button } from "@/design-system";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { useStripePayment } from "@/hooks/useStripePayment";
import { useCredits } from "@/hooks/useCreditBalance";
import { useInvoices, type Invoice } from "@/hooks/useInvoices";
import { calculateCreditPrice } from "@/config/stripe";
import { BuyCreditModal } from "@/components/credits/BuyCreditModal";
import { Skeleton } from "@/components/ui/skeleton";

type PeriodFilter = '7days' | '30days' | '3months' | '6months' | '1year' | 'all';
type SortColumn = 'id' | 'date' | 'amount';
type SortDirection = 'asc' | 'desc';

export const SubscriptionManagerV3 = () => {
  const [activeTab, setActiveTab] = useState(() => {
    // Vérifier les paramètres URL pour ouvrir le bon onglet
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'apercu';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [billingMode, setBillingMode] = useState<'premium' | 'payasyougo'>('premium');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [creditQuantity, setCreditQuantity] = useState(1);
  const [showSuccessMessage, setShowSuccessMessage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('payment') === 'success';
  });
  
  // États pour le tri et filtrage des factures
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  const { organizationData, loading } = useSubscriptionFeatures();
  const { availableCredits } = useCredits(organizationData?.id || '');
  const { 
    error: paymentError, 
    toggleBrandingPro,
    updateSeats,
    purchaseCredits,
    loading: paymentLoading
  } = useStripePayment();
  const { invoices, loading: invoicesLoading, downloadInvoice, refetch: refetchInvoices } = useInvoices(organizationData?.id || '');

  // Recharger les factures après un paiement réussi
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment') === 'success' || urlParams.get('success') === 'true';
    
    if (paymentSuccess && organizationData?.id) {
      // Attendre 2 secondes que Stripe crée la facture
      const timer = setTimeout(() => {
        refetchInvoices();
        // Ouvrir l'onglet factures
        setActiveTab('factures');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    return undefined;
  }, [organizationData?.id, refetchInvoices]);

  // Options de période
  const periodOptions = [
    { value: '7days' as const, label: '7 derniers jours' },
    { value: '30days' as const, label: '30 derniers jours' },
    { value: '3months' as const, label: '3 derniers mois' },
    { value: '6months' as const, label: '6 derniers mois' },
    { value: '1year' as const, label: '1 an' },
    { value: 'all' as const, label: 'Toutes les périodes' },
  ];

  // Helper pour parser la date française (format: "20/11/2025")
  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Fonction de filtrage par période
  const filterByPeriod = (invoiceList: Invoice[]) => {
    if (periodFilter === 'all') return invoiceList;
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (periodFilter) {
      case '7days':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '3months':
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      case '6months':
        cutoffDate.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
    }
    
    return invoiceList.filter((inv: Invoice) => {
      const invoiceDate = parseDate(inv.date);
      return invoiceDate >= cutoffDate;
    });
  };

  // Fonction de tri
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Inverser la direction si même colonne
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne, commencer par desc
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Composant icône de tri
  const SortIcon = ({ column }: { column: SortColumn }): JSX.Element => {
    if (sortColumn !== column) {
      // Icône neutre (double flèche)
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    // Icône active (flèche simple)
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-[#40E0D0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-[#40E0D0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Pipeline de traitement des factures
  // 1. Filtrage par recherche texte
  const searchFilteredInvoices = invoices.filter(inv => 
    inv.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 2. Filtrage par période
  const periodFilteredInvoices = filterByPeriod(searchFilteredInvoices);

  // 3. Tri par colonne
  const sortedInvoices = [...periodFilteredInvoices].sort((a, b) => {
    let comparison = 0;
    
    switch (sortColumn) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'date':
        comparison = parseDate(a.date).getTime() - parseDate(b.date).getTime();
        break;
      case 'amount':
        comparison = a.amount - b.amount;
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const filteredInvoices = sortedInvoices;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-4 w-64 mb-6" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const orgId = organizationData?.id || '';
  const brandingProEnabled = organizationData?.features.brandingProEnabled || false;
  const totalSeats = organizationData?.features.totalSeats || 1;

  return (
    <div className="w-full">
      {/* Affichage du succès */}
      {showSuccessMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded-full flex-shrink-0"></div>
            <p className="text-sm text-green-700">Paiement réussi ! Vos crédits ont été ajoutés à votre compte.</p>
            <button 
              onClick={() => setShowSuccessMessage(false)}
              className="ml-auto text-green-500 hover:text-green-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Affichage des erreurs */}
      {(paymentError || localError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded-full flex-shrink-0"></div>
            <p className="text-sm text-red-700">{paymentError || localError}</p>
            <button 
              onClick={() => setLocalError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Navigation par onglets - Style exact des screens */}
      <div className="border-b border-gray-200 mb-8">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('apercu')}
            className={`pb-3 px-1 border-b-2 transition-colors text-sm font-semibold ${
              activeTab === 'apercu'
                ? 'text-[#40E0D0]'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            style={activeTab === 'apercu' ? { borderBottomColor: '#40E0D0' } : { borderBottomColor: 'transparent' }}
          >
            Aperçu
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`pb-3 px-1 border-b-2 transition-colors text-sm font-semibold ${
              activeTab === 'plans'
                ? 'text-[#40E0D0]'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            style={activeTab === 'plans' ? { borderBottomColor: '#40E0D0' } : { borderBottomColor: 'transparent' }}
          >
            Plans & Tarifs
          </button>
          <button
            onClick={() => setActiveTab('factures')}
            className={`pb-3 px-1 border-b-2 transition-colors text-sm font-semibold ${
              activeTab === 'factures'
                ? 'text-[#40E0D0]'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            style={activeTab === 'factures' ? { borderBottomColor: '#40E0D0' } : { borderBottomColor: 'transparent' }}
          >
            Factures
          </button>
        </div>
      </div>

      {/* Onglet Aperçu */}
      {activeTab === 'apercu' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-medium text-gray-900">Votre plan actuel</h2>
          </div>
          
          <div className="flex gap-8">
            <div className="flex-1">
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-500">Options Premium</p>
                <p className="text-base font-semibold text-gray-900">
                  {brandingProEnabled ? 'Branding Pro activé' : 'Aucune option premium'}
                </p>
                <p className="text-sm text-gray-700">
                  {totalSeats} place{totalSeats > 1 ? 's' : ''} • {brandingProEnabled ? 'Marque blanche' : 'Standard'}
                </p>
              </div>
            </div>
            
            <div className="w-px bg-gray-200"></div>
            
            <div className="flex-1">
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <p className="text-sm text-gray-500">Crédits</p>
                </div>
                <p className="text-base font-medium text-gray-900">{availableCredits} crédits restants</p>
                <p className="text-sm text-gray-700">Tarif : 15€/crédit (Pay-as-you-go)</p>
              </div>
              <button 
                onClick={() => setShowCreditModal(true)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 uppercase hover:bg-gray-50 transition-colors"
              >
                Acheter des crédits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Plans & Tarifs */}
      {activeTab === 'plans' && (
        <div className="flex flex-col gap-10">
          {/* Header avec toggle - Style exact des screens */}
          <div className="w-full flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <h2 className="text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
                {billingMode === 'premium' ? 'Options Premium' : 'Packs de crédits'}
              </h2>
              <p className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                {billingMode === 'premium' 
                  ? 'Branding Pro et seats supplémentaires.'
                  : 'Payez uniquement ce que vous utilisez. Idéal pour débuter.'}
              </p>
            </div>
            <div className="flex justify-start items-center gap-4">
              <span className={`text-base font-medium font-['Bricolage_Grotesque'] ${billingMode === 'premium' ? 'text-gray-800' : 'text-neutral-400'}`}>
                Options Premium
              </span>
              <button
                onClick={() => setBillingMode(billingMode === 'premium' ? 'payasyougo' : 'premium')}
                className={`w-12 h-7 p-1 rounded-[55.36px] flex justify-start items-center overflow-hidden transition-colors ${
                  billingMode === 'payasyougo' ? 'bg-teal-400' : 'bg-gray-300'
                }`}
              >
                <div className={`w-[22px] h-[22px] rounded-full bg-white transition-transform ${
                  billingMode === 'payasyougo' ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
              <span className={`text-base font-medium font-['Bricolage_Grotesque'] ${billingMode === 'payasyougo' ? 'text-gray-800' : 'text-neutral-400'}`}>
                Payer par crédit
              </span>
            </div>
          </div>
     
     {billingMode === 'premium' ? (
            /* Options Premium - Style exact des screens */
            <div className="space-y-6">
              {/* Branding Pro */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Branding Pro</h3>
                    <p className="text-sm text-gray-500">30€/mois • Marque blanche complète</p>
                  </div>
                  <Button
                    variant={brandingProEnabled ? "outline" : "primary"}
                    size="sm"
                    onClick={() => toggleBrandingPro(orgId, !brandingProEnabled)}
                    loading={paymentLoading}
                  >
                    {brandingProEnabled ? 'Désactiver' : 'Activer'}
                  </Button>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-[#40E0D0] bg-white flex items-center justify-center">
                      <div className="w-2 h-2 bg-[#40E0D0] rounded-full"></div>
                    </div>
                    Logo personnalisé
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-[#40E0D0] bg-white flex items-center justify-center">
                      <div className="w-2 h-2 bg-[#40E0D0] rounded-full"></div>
                    </div>
                    Couleurs de marque
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-[#40E0D0] bg-white flex items-center justify-center">
                      <div className="w-2 h-2 bg-[#40E0D0] rounded-full"></div>
                    </div>
                    Domaine personnalisé
                  </li>
                </ul>
              </div>

              {/* Seats supplémentaires */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Seats supplémentaires</h3>
                    <p className="text-sm text-gray-500">19€/mois par seat • 1 seat inclus gratuitement</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{totalSeats} seats au total</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateSeats(orgId, 1)}
                      loading={paymentLoading}
                    >
                      Ajouter +1
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>• 1 seat inclus gratuitement</p>
                  <p>• {Math.max(0, totalSeats - 1)} seats supplémentaires</p>
                  <p>• {organizationData?.usedSeats || 0} seats utilisés</p>
                </div>
              </div>
            </div>
          ) : (
            /* Mode Pay-as-you-go - Packs de crédits avec style des screens */
            <div className="flex flex-col gap-6">
              {/* Avertissement Mode Pay-as-you-go */}
              <div className="bg-white border border-[#40E0D0]/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-[#40E0D0]" />
                  <h3 className="text-gray-800 text-base font-semibold font-['Bricolage_Grotesque']">Mode Pay-as-you-go</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] mb-2">
                      Accès complet aux fonctionnalités essentielles :
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-600 font-['Bricolage_Grotesque']">
                      <li>• Tableau de bord & statistiques</li>
                      <li>• Rapports automatisés</li>
                      <li>• Relances automatiques</li>
                      <li>• 1 place utilisateur</li>
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                      <span className="font-semibold">Non inclus :</span> 
                      <span className="font-normal"> Branding personnalisé • Multi-sièges</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Achat de crédits flexible */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Acheter des crédits</h3>
                    <p className="text-sm text-gray-500">15€ par crédit • Choisissez la quantité qui vous convient</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de crédits
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={creditQuantity}
                      onChange={(e) => setCreditQuantity(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                      placeholder="Entrez le nombre de crédits"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix total
                    </label>
                    <div className="text-2xl font-semibold text-[#40E0D0]">
                      {calculateCreditPrice(creditQuantity).total.toLocaleString()}€
                    </div>
                    <div className="text-sm text-gray-500">
                      {calculateCreditPrice(creditQuantity).unit_price}€ par crédit
                      {calculateCreditPrice(creditQuantity).savings > 0 && (
                        <span className="text-green-600 ml-2">
                          (Économie: {calculateCreditPrice(creditQuantity).savings}€)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => {
                        if (!orgId) {
                          setLocalError('Organisation non trouvée. Veuillez recharger la page.');
                          return;
                        }
                        if (creditQuantity < 1 || creditQuantity > 1000) {
                          setLocalError('La quantité doit être entre 1 et 1000 crédits.');
                          return;
                        }
                        purchaseCredits(orgId, creditQuantity);
                      }}
                      disabled={paymentLoading || !orgId || creditQuantity < 1}
                      className="px-6 py-3 bg-teal-400 text-white rounded-lg font-semibold hover:bg-teal-500 transition-colors disabled:bg-gray-300"
                    >
                      {paymentLoading ? 'Traitement...' : 'Acheter'}
                    </button>
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  • 1 crédit = 1 campagne de prise de références complète
                  • Paiement sécurisé via Stripe
                  • Crédits ajoutés instantanément après paiement
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* O
nglet Factures - Style exact des screens */}
      {activeTab === 'factures' && (
        <>
          {/* Header avec recherche */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-medium text-gray-900 mb-1">Historique des factures</h2>
              <p className="text-sm text-gray-500">Toutes vos factures et reçus de paiement</p>
            </div>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#40E0D0' }} />
                <input
                  type="text"
                  placeholder="Rechercher la facture ici......."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56 pl-9 pr-3 py-2.5 bg-white rounded-lg border border-gray-200 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all"
                  onFocus={(e) => (e.target as HTMLInputElement).style.boxShadow = `0 0 0 2px #40E0D040`}
                  onBlur={(e) => (e.target as HTMLInputElement).style.boxShadow = 'none'}
                />
              </div>
              <div className="relative">
                <button 
                  onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                  className="px-3 py-2.5 bg-white rounded-lg border border-gray-200 flex items-center gap-2 text-xs font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <Calendar className="w-4 h-4" style={{ color: '#40E0D0' }} />
                  {periodOptions.find(opt => opt.value === periodFilter)?.label}
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d={showPeriodDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                  </svg>
                </button>
                
                {showPeriodDropdown && (
                  <>
                    {/* Overlay pour fermer le dropdown */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowPeriodDropdown(false)}
                    />
                    
                    {/* Dropdown */}
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-20">
                      {periodOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setPeriodFilter(option.value);
                            setShowPeriodDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            periodFilter === option.value ? 'text-[#40E0D0] font-medium bg-[#40E0D0]/5' : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                          {periodFilter === option.value && (
                            <span className="float-right">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tableau des factures */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {invoicesLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40E0D0] mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Chargement des factures...</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-500">Aucune facture trouvée</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-5 py-4 text-left text-sm font-medium text-gray-500">
                      <button 
                        onClick={() => handleSort('id')}
                        className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                      >
                        Numéro de facture
                        <SortIcon column="id" />
                      </button>
                    </th>
                    <th className="px-5 py-4 text-left text-sm font-medium text-gray-500">
                      <button 
                        onClick={() => handleSort('date')}
                        className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                      >
                        Date
                        <SortIcon column="date" />
                      </button>
                    </th>
                    <th className="px-5 py-4 text-left text-sm font-medium text-gray-500">
                      <button 
                        onClick={() => handleSort('amount')}
                        className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                      >
                        Montant
                        <SortIcon column="amount" />
                      </button>
                    </th>
                    <th className="px-5 py-4 text-left text-sm font-medium text-gray-500">Statut</th>
                    <th className="px-5 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice, idx) => (
                    <tr 
                      key={invoice.stripe_id} 
                      className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                        idx === filteredInvoices.length - 1 ? 'border-b-0' : ''
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invoice.id}</p>
                          <p className="text-xs text-gray-500">{invoice.description}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-900">{invoice.date}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-900">{invoice.amount}€</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded-md border text-xs font-medium ${
                          invoice.status === 'paid' 
                            ? 'border-green-500 text-green-500'
                            : invoice.status === 'pending'
                            ? 'border-yellow-500 text-yellow-500'
                            : 'border-red-500 text-red-500'
                        }`}>
                          {invoice.status === 'paid' ? 'Payé' : 
                           invoice.status === 'pending' ? 'En attente' : 
                           invoice.status === 'failed' ? 'Échec' : 'Annulé'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button 
                          onClick={() => downloadInvoice(invoice)}
                          className="px-2 py-1.5 rounded-lg border border-gray-200 flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:bg-gray-100 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Télécharger
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Modal d'achat de crédits - Version unifiée */}
      <BuyCreditModal
        open={showCreditModal}
        onOpenChange={setShowCreditModal}
      />
    </div>
  );
};